// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2024-09-04 17:31:22 +0200 (Wed, 04 Sep 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 145415 $
//  Purpose   : transcription parts
// =============================================================================
// ---Third-party imports
import React from 'react'
import {differenceInDays} from 'date-fns'

// ---Framework imports
import {dict, includes} from 'ldash'
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import {formatDate, formatDateTime} from 'uix/utils'
import {useApi} from 'uix/hooks'
import {Button, Header} from 'semantic-ui-react'
import {loadLazy as ll, funcPart} from 'uix/parts'

// ---Local imports
import {TranscriptionLayout, TranscriptionWiki, BasicButton, UserInfo} from '../components'
import {useApiWithPuid} from '../hooks'
import {TranslatedTable, VButton, VIcon} from 'uix/components'

// ---Exports
export {TranscriptionAccount} from './account'
export {TranscriptionActivity} from './activity'

export const TranscriptionEditor = ll(() =>
  import(/* webpackChunkName: 'transcription' */ './editor')
)

export const TranscriptionWikipage = ({partcfg}) => {
  return (
    <TranscriptionLayout>
      <TranscriptionWiki ident={partcfg.ident} />
    </TranscriptionLayout>
  )
}

export const TranscriptionUsers = ({i18n}) => {
  let {data} = useApi('filter/getTranscriptionUsers')

  let transcribers = data?.transcribers ?? []
  const formatValue = (i, format) => {
    if (format === 'date') {
      return formatDateTime(i)
    } else if (format === 'boolean') {
      if (i === 'true' || i === 'True') {
        return <VIcon color="green" name="check" />
      } else return <VIcon color="red" name="x" />
    }
    return i
  }

  let rowActions = row => {
    let {fullname, last_login, login_email} = row
    let testConfirm = (
      <>
        {fullname}
        <br></br>
        {formatDateTime(last_login)}
        <br></br>
        {login_email}
      </>
    )

    return (
      <span style={{float: 'right'}}>
        <VButton
          onClick={() => {}}
          confirm={testConfirm}
          size="small"
          compact
          secondary
          icon="eye"
          title="View Row"
        />
      </span>
    )
  }

  let columns = [
    {
      accessor: 'fullname',
      Header: `${i18n.get('name')}`,
      filterable: true,
      filterMethod: (filter, row) => customSimpleFilter(filter, row),
      aggregate: vals => '',
    },
    {
      accessor: 'state',
      Header: 'Bestätigt',
      Cell: ({value}) => formatValue(String(value == 10), 'boolean'),
      groupable: true,
      PivotValue: ({value, subRows}) => (
        <>
          {formatValue(String(value == 10), 'boolean')} {subRows.length}
        </>
      ),
      aggregate: vals => '',
    },
    {
      accessor: 'last_login',
      Header: `${i18n.get('lastLogin')}`,
      Cell: ({value}) => formatValue(value, 'date'),
      aggregate: vals => '',
    },
    {
      accessor: 'name_visible',
      Header: `${i18n.get('showName')}`,
      Cell: ({value}) => formatValue(value, 'boolean'),
      aggregate: vals => '',
    },
    {accessor: 'login_email', Header: `${i18n.get('email')}`, aggregate: vals => ''},
    {
      id: 'actions',
      Header: `${i18n.get('action')}`,
      Cell: ({row}) => rowActions(row),
      sortable: false,
      aggregate: vals => '',
    },
  ]

  return (
    <TranscriptionLayout>
      <Header>{i18n.span('registeredUsers')}</Header>
      <TranslatedTable columns={columns} data={transcribers} selectionKey="puid" showPagination />
    </TranscriptionLayout>
  )
}

export const TranscriptionPublished = ({i18n}) => {
  let {data} = useApiWithPuid('filter/getTranscriptionPublished')
  let {pages = [], metadatas = [], navigator} = data?.transcriptionPublished ?? {}
  let otid2md = dict(metadatas.map(md => [md.otid, md]))

  let rowActions = row => {
    return (
      <>
        <BasicButton icon="eye" title={i18n.get('viewWiki')} />
        <BasicButton icon="history" title={i18n.get('histWiki')} />
        <BasicButton
          icon={{color: 'red', name: 'delete'}}
          title={i18n.get('deleteTranscription')}
        />
      </>
    )
  }

  let columns = [
    {
      accessor: 'savepointTs',
      Header: 'Publiziert am',
      Cell: ({value}) => formatDate(value),
    },
    {
      accessor: 'caption',
      Header: 'Titel / Seite',
      Cell: ({original}) => (
        <>
          {otid2md[original.md_otid]?.caption} / {original.caption}
        </>
      ),
      sortMethod: (a, b, desc, row_a, row_b) => sortByOtid2md(row_a, row_b, metadatas),
      filterMethod: (filter, row) => filterByOtid2md(filter, row, metadatas),
      filterable: true,
    },
    {
      accessor: 'fullname',
      Header: 'Freigegeben durch',
      filterable: true,
      filterMethod: (filter, row) => customSimpleFilter(filter, row),
    },
    {
      accessor: 'authors',
      filterMethod: (filter, row) => customSimpleFilter(filter, row, 'fullname'),
      filterable: true,
      sortMethod: (a, b) => sortByValue(a, b, 'fullname'),
      Header: 'Autor',
      Cell: ({value}) => (
        <>
          {value.map(({fullname, email, puid}) => (
            <UserInfo key={puid} fullname={fullname} email={email} puid={puid} />
          ))}
        </>
      ),
    },
    {
      id: 'actions',
      Header: `${i18n.get('action')}`,
      sortable: false,
      Cell: ({row}) => rowActions(row),
    },
  ]

  return (
    <TranscriptionLayout>
      <Header>{i18n.span('transcriptionsPublished')}</Header>
      <TranslatedTable columns={columns} data={pages} selectionKey="otid" showPagination />
    </TranscriptionLayout>
  )
}

export const TranscriptionBegun = ({i18n}) => {
  let {data} = useApiWithPuid('filter/getTranscriptionBegun')
  let {pages = [], metadatas = [], navigator} = data?.transcriptionBegun ?? {}
  let otid2md = dict(metadatas.map(md => [md.otid, md]))

  let rowActions = row => {
    return (
      <>
        <BasicButton icon="eye" title={i18n.get('viewWiki')} />
        <BasicButton icon="history" title={i18n.get('histWiki')} />
        <BasicButton icon="undo" title={i18n.get('revertTranscription')} />
        <BasicButton
          icon={{color: 'green', name: 'arrow alternate circle right outline'}}
          title={i18n.get('reopenTranscriptionTo')}
        />
      </>
    )
  }

  let columns = [
    {
      accessor: 'savepointTs',
      Header: 'Tage unbearbeitet',
      Cell: ({value}) => (
        <span style={{display: 'flex', justifyContent: 'space-between'}}>
          <span>{differenceInDays(new Date(), new Date(value))}</span>
          <span style={{opacity: '0.75', margin: '0 2rem 0 .5rem'}}>{formatDateTime(value)}</span>
        </span>
      ),
    },
    {
      accessor: 'caption',
      Header: 'Titel / Seite',
      Cell: ({original}) => (
        <>
          {otid2md[original.md_otid]?.caption} / {original.caption}
        </>
      ),
      sortMethod: (a, b, desc, row_a, row_b) => sortByOtid2md(row_a, row_b, metadatas),
      filterMethod: (filter, row) => filterByOtid2md(filter, row, metadatas),
      filterable: true,
    },
    {
      accessor: 'fullname',
      Header: 'Autor',
      filterMethod: (filter, row) => customSimpleFilter(filter, row),
      filterable: true,
      Cell: ({original}) => (
        <UserInfo fullname={original?.fullname} email={original?.email} puid={original?.puid} />
      ),
    },
    {
      id: 'actions',
      Header: `${i18n.get('action')}`,
      sortable: false,
      Cell: ({row}) => rowActions(row),
    },
  ]

  return (
    <TranscriptionLayout>
      <Header>{i18n.span('transcriptionsBegunTimeline')}</Header>
      <TranslatedTable columns={columns} data={pages} selectionKey="otid" showPagination />
    </TranscriptionLayout>
  )
}

export const TranscriptionReview = ({i18n}) => {
  let {data} = useApiWithPuid('filter/getTranscriptionReview')
  let transcriptionReview = data?.transcriptionReview || []
  let items = transcriptionReview.map(({otid, caption, pages}) => {
    let trs = pages.map(({otid, caption, fullname, email, puid, odid}) => (
      <tr key={otid}>
        <td>{caption}</td>
        <td>
          <UserInfo fullname={fullname} email={email} puid={puid} showActivityButton />
        </td>
        <td>
          <BasicButton
            icon={{color: 'orange', name: 'arrow alternate circle left outline'}}
            title={i18n.get('reopenTranscription')}
            onClick={() => '/zuzcmi/action/reopenTranscription?otid=3531313&puid=183'}
          />
          <BasicButton
            icon={{color: 'green', name: 'arrow alternate circle up outline'}}
            title={i18n.get('setReleasepoint')}
            onClick={() => '/zuzcmi/action/setReleasepoint?otid=3531313'}
          />
          <BasicButton
            icon="eye"
            title={i18n.get('viewWiki')}
            onClick={() => '/zuzcmi/content/pagetext/3531313'}
          />
        </td>
      </tr>
    ))
    return (
      <div key={otid}>
        <h3>{caption}</h3>
        <table style={{width: '100%'}}>
          <tbody>{trs}</tbody>
        </table>
      </div>
    )
  })
  return (
    <TranscriptionLayout>
      <Header>{i18n.span('transcriptionReview')}</Header>
      {items}
    </TranscriptionLayout>
  )
}

const compareStrings = (a, b) => {
  // force null and undefined to the bottom
  a = a === null || a === undefined ? '' : a
  b = b === null || b === undefined ? '' : b
  // force any string values to lowercase
  a = typeof a === 'string' ? a.toLowerCase() : a
  b = typeof b === 'string' ? b.toLowerCase() : b
  // Return 0, 1 or -1 to indicate a sort priority
  return a.localeCompare(b)
}

const sortByOtid2md = (row_a, row_b, metadatas) => {
  let otid2md = dict(metadatas.map(md => [md.otid, md]))
  let a = otid2md[row_a._original.md_otid]?.caption || ''
  let b = otid2md[row_b._original.md_otid]?.caption || ''
  return compareStrings(a, b)
}
const sortByValue = (a, b, value) => {
  a = a.map(item => item[value]).join(', ')
  b = b.map(item => item[value]).join(', ')
  return compareStrings(a, b)
}

const filterByOtid2md = (filter, row, metadatas) => {
  let otid2md = dict(metadatas.map(md => [md.otid, md]))
  let str = otid2md[row._original.md_otid]?.caption.toLowerCase() || ''
  return str.includes(filter.value.toLowerCase())
}

const customSimpleFilter = (filter, row, value) => {
  let str = null
  if (value) {
    if (Array.isArray(row[filter.id])) {
      str = row[filter.id]
        .map(item => item[value])
        .join(', ')
        .toLowerCase()
    } else {
      str = row[filter.id][value].toLowerCase()
    }
  } else {
    if (Array.isArray(row[filter.id])) {
      str = row[filter.id].join(', ').toLowerCase()
    } else {
      str = row[filter.id].toLowerCase()
    }
  }
  return str.includes(filter.value.toLowerCase())
}
